// Deterministic, explainable scoring (PRD §13 Module A + §17 "scoring explainability").
// Every score returns its component breakdown; the UI renders the breakdown verbatim.

export interface ScoreComponent {
  key: string;
  label: string;
  points: number;
  max: number;
  why: string;
}

export interface ScoredResult {
  score: number; // 0–100
  components: ScoreComponent[];
}

export interface IntakeScoringInput {
  budget_range?: string | null;
  timeline?: string | null;
  role_level?: string | null;
  company_size?: string | null;
  primary_goal?: string | null;
  offer_interest?: string | null;
  ai_maturity_self_rating?: number | null;
  regulatory_exposure?: string | null;
  compelling_event?: string | null;
}

const BUDGET_POINTS: Record<string, [number, string]> = {
  under_5k: [6, 'Budget under $5k — advisory-sized'],
  '5k_15k': [12, 'Budget $5k–15k — fits a sprint package'],
  '15k_50k': [20, 'Budget $15k–50k — fits multi-week packages'],
  '50k_plus': [25, 'Budget $50k+ — fits full engagement'],
  unsure: [8, 'Budget not yet defined'],
};

const TIMELINE_POINTS: Record<string, [number, string]> = {
  asap: [20, 'Wants to start immediately'],
  this_quarter: [15, 'Targeting this quarter'],
  this_year: [8, 'Targeting this year'],
  exploring: [3, 'Exploring, no committed timeline'],
};

const ROLE_POINTS: Record<string, [number, string]> = {
  exec: [20, 'Executive sponsor engaged directly'],
  director: [16, 'Director-level — strong buying influence'],
  manager: [12, 'Manager-level — needs sponsor development'],
  ic: [6, 'Individual contributor — champion, not buyer'],
  other: [8, 'Role level unclear'],
};

const SIZE_POINTS: Record<string, [number, string]> = {
  '1-10': [6, 'Micro org — smaller engagement scope'],
  '11-50': [10, 'Small org — fast decisions, focused scope'],
  '51-200': [15, 'Mid-market sweet spot for packaged consulting'],
  '201-1000': [13, 'Upper mid-market — multi-stakeholder'],
  '1000_plus': [9, 'Enterprise — longer cycles, larger ceiling'],
};

export function computeFitScore(input: IntakeScoringInput): ScoredResult {
  const components: ScoreComponent[] = [];
  const pick = (
    map: Record<string, [number, string]>,
    raw: string | null | undefined,
    key: string,
    label: string,
    max: number,
  ) => {
    const hit = raw ? map[raw] : undefined;
    components.push({
      key,
      label,
      points: hit ? hit[0] : 0,
      max,
      why: hit ? hit[1] : 'Not provided',
    });
  };

  pick(BUDGET_POINTS, input.budget_range, 'budget', 'Budget range', 25);
  pick(TIMELINE_POINTS, input.timeline, 'timeline', 'Timeline', 20);
  pick(ROLE_POINTS, input.role_level, 'role', 'Contact seniority', 20);
  pick(SIZE_POINTS, input.company_size, 'size', 'Company size', 15);

  const goal = (input.primary_goal ?? '').trim();
  const goalPts = goal.length >= 120 ? 10 : goal.length >= 40 ? 7 : goal.length > 0 ? 3 : 0;
  components.push({
    key: 'goal',
    label: 'Goal clarity',
    points: goalPts,
    max: 10,
    why:
      goalPts >= 10
        ? 'Detailed, specific goal statement'
        : goalPts >= 7
          ? 'Clear goal statement'
          : goalPts > 0
            ? 'Goal stated but thin'
            : 'No goal provided',
  });

  const hasPackage = !!input.offer_interest && input.offer_interest !== 'not_sure';
  components.push({
    key: 'package',
    label: 'Package interest',
    points: hasPackage ? 10 : 4,
    max: 10,
    why: hasPackage ? 'Selected a specific service package' : 'Not sure which offering fits yet',
  });

  const score = Math.min(
    100,
    components.reduce((s, c) => s + c.points, 0),
  );
  return { score, components };
}

export function computeUrgencyScore(input: IntakeScoringInput): ScoredResult {
  const components: ScoreComponent[] = [];
  const timelineMap: Record<string, [number, string]> = {
    asap: [55, 'Immediate start requested'],
    this_quarter: [40, 'This-quarter target'],
    this_year: [18, 'This-year target'],
    exploring: [5, 'Exploring only'],
  };
  const t = input.timeline ? timelineMap[input.timeline] : undefined;
  components.push({
    key: 'timeline',
    label: 'Timeline',
    points: t ? t[0] : 0,
    max: 55,
    why: t ? t[1] : 'Not provided',
  });

  const event = (input.compelling_event ?? '').trim();
  components.push({
    key: 'event',
    label: 'Compelling event',
    points: event.length > 10 ? 25 : 0,
    max: 25,
    why: event.length > 10 ? 'Named a concrete driver/date' : 'No compelling event described',
  });

  const budgeted = !!input.budget_range && input.budget_range !== 'unsure';
  components.push({
    key: 'budget',
    label: 'Budget readiness',
    points: budgeted ? 20 : 5,
    max: 20,
    why: budgeted ? 'Budget range already scoped' : 'Budget not yet scoped',
  });

  const score = Math.min(100, components.reduce((s, c) => s + c.points, 0));
  return { score, components };
}

export const AUTO_QUALIFY_THRESHOLD = 60;

export function classifyQualification(fitScore: number): 'auto_qualified' | 'needs_review' {
  return fitScore >= AUTO_QUALIFY_THRESHOLD ? 'auto_qualified' : 'needs_review';
}

// ---------------------------------------------------------------------------
// Recommended offer (one of the 5 service packages) from intake signals.

export interface OfferRecommendation {
  packageId: string;
  signals: string[];
}

const KEYWORDS: Array<{ packageId: string; words: RegExp; signal: string }> = [
  {
    packageId: 'governance_foundation',
    words: /\b(governan|risk|policy|policies|complian|regulat|audit|privacy|nist|iso 42001|responsible ai|shadow ai)\w*/i,
    signal: 'Goal mentions governance/risk/compliance themes',
  },
  {
    packageId: 'engineering_architecture',
    words: /\b(agent|agentic|rag|retrieval|mcp|architect|llm app|chatbot|copilot build|orchestrat|vector|model selection)\w*/i,
    signal: 'Goal mentions agents/RAG/MCP/architecture themes',
  },
  {
    packageId: 'ai_sdlc',
    words: /\b(developer|dev team|sdlc|code|coding|copilot|cursor|claude code|engineering team|delivery|design[- ]to[- ]code|productivity)\w*/i,
    signal: 'Goal mentions developer productivity / AI SDLC themes',
  },
  {
    packageId: 'automation_roadmap',
    words: /\b(automat|workflow|use case|roadmap|roi|opportunit|process|efficien|cost sav)\w*/i,
    signal: 'Goal mentions automation/use-case/ROI themes',
  },
];

export function recommendOffer(input: IntakeScoringInput): OfferRecommendation {
  const signals: string[] = [];
  const goal = input.primary_goal ?? '';

  // Explicit package interest wins.
  if (input.offer_interest && input.offer_interest !== 'not_sure') {
    return {
      packageId: input.offer_interest,
      signals: ['Prospect selected this package on the intake form'],
    };
  }

  // Regulatory exposure pushes governance-first.
  if (input.regulatory_exposure === 'regulated') {
    signals.push('Operates in a regulated industry');
    return { packageId: 'governance_foundation', signals };
  }

  for (const k of KEYWORDS) {
    if (k.words.test(goal)) {
      signals.push(k.signal);
      return { packageId: k.packageId, signals };
    }
  }

  // Low self-rated maturity → start with readiness.
  if ((input.ai_maturity_self_rating ?? 3) <= 2) {
    signals.push('Self-rated AI maturity is low (≤2/5)');
    return { packageId: 'readiness_sprint', signals };
  }

  signals.push('No strong signal — defaulting to readiness assessment');
  return { packageId: 'readiness_sprint', signals };
}

// ---------------------------------------------------------------------------
// Use-case portfolio scoring (PRD §9 Stage 4 / Module D).

export interface UseCaseScores {
  value_score: number;
  feasibility_score: number;
  readiness_score: number;
  risk_score: number;          // higher = riskier
  time_to_value_score: number; // higher = faster
}

export type PriorityBand = 'quick_win' | 'strategic_bet' | 'governance_first' | 'evaluate' | 'defer';
export type RiskTier = 'low' | 'medium' | 'high';

export interface UseCasePriority {
  priority: number; // 0–100
  band: PriorityBand;
  riskTier: RiskTier;
  recommendedAction: string;
}

export function scoreUseCase(s: UseCaseScores): UseCasePriority {
  const clamp = (n: number) => Math.max(0, Math.min(5, n));
  const value = clamp(s.value_score);
  const feas = clamp(s.feasibility_score);
  const ready = clamp(s.readiness_score);
  const risk = clamp(s.risk_score);
  const ttv = clamp(s.time_to_value_score);

  // Weighted priority: value 35%, feasibility 20%, readiness 15%, speed 15%, safety 15%.
  const priority = Math.round(
    ((value * 0.35 + feas * 0.2 + ready * 0.15 + ttv * 0.15 + (5 - risk) * 0.15) / 5) * 100,
  );

  const riskTier: RiskTier = risk >= 4 ? 'high' : risk >= 3 ? 'medium' : 'low';

  let band: PriorityBand;
  if (risk >= 4) band = 'governance_first';
  else if (value >= 3.5 && feas >= 3.5 && ttv >= 3.5 && risk <= 2) band = 'quick_win';
  else if (value >= 4) band = 'strategic_bet';
  else if (value < 2.5) band = 'defer';
  else band = 'evaluate';

  const actionByBand: Record<PriorityBand, string> = {
    quick_win: 'Pilot in the next 30 days with a named owner and a baseline metric.',
    strategic_bet: 'Scope architecture and data dependencies; plan a 60–90 day build.',
    governance_first: 'Resolve risk controls and obtain a documented go/no-go before any build.',
    evaluate: 'Clarify value hypothesis and feasibility; re-score after discovery.',
    defer: 'Park it — revisit when value or readiness materially changes.',
  };

  return { priority, band, riskTier, recommendedAction: actionByBand[band] };
}

export const BAND_LABELS: Record<PriorityBand, string> = {
  quick_win: 'Quick Win',
  strategic_bet: 'Strategic Bet',
  governance_first: 'Governance First',
  evaluate: 'Evaluate',
  defer: 'Defer',
};
